import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import PractitionerDashboardView from "@/components/PractitionerDashboardView";

const DoctorHome = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const bn = language === "bn";
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<any[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoadError(null);
      const { data: reqData, error } = await supabase
        .from("consultation_requests")
        .select("*")
        .eq("practitioner_id", user.id)
        .eq("practitioner_type", "doctor")
        .order("created_at", { ascending: false });

      if (error || !reqData) {
        setLoadError(error?.message ?? "Could not load requests");
        setLoading(false);
        return;
      }

      const patientIds = [...new Set(reqData.map((r) => r.patient_id))];
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", patientIds);
      if (profileError) {
        setLoadError(profileError.message);
        setLoading(false);
        return;
      }

      const profileMap: Record<string, string> = {};
      (profileData ?? []).forEach((p) => { profileMap[p.user_id] = p.full_name; });

      setRequests(reqData.map((r) => ({ ...r, patient: { full_name: profileMap[r.patient_id] ?? "" } })));
      setLoading(false);
    };
    load();
  }, [user]);

  const handleRequest = async (id: string, action: "accepted" | "rejected") => {
    if (processingId) return;
    setProcessingId(id);
    let { error } = await supabase.rpc("respond_to_consultation_request", {
      _request_id: id,
      _action: action,
    });

    // Older deployed databases may not have the RPC yet. Keep the action
    // usable until the compatibility migration is applied.
    if (error && /function .*respond_to_consultation_request|schema cache/i.test(error.message)) {
      const request = requests.find((item) => item.id === id);
      if (request) {
        const requestUpdate = await supabase
          .from("consultation_requests")
          .update({ status: action })
          .eq("id", id)
          .eq("practitioner_id", user?.id);
        error = requestUpdate.error;
        if (!error && action === "accepted") {
          const { data: conversation } = await supabase
            .from("conversations")
            .select("id, fee")
            .eq("patient_id", request.patient_id)
            .eq("practitioner_id", user?.id)
            .eq("practitioner_type", "doctor")
            .maybeSingle();
          const conversationUpdate = await supabase
            .from("conversations")
            .update({
              status: "accepted",
              ...(Number(conversation?.fee ?? 0) === 0 ? { paid: true } : {}),
            })
            .eq("patient_id", request.patient_id)
            .eq("practitioner_id", user?.id)
            .eq("practitioner_type", "doctor");
          error = conversationUpdate.error;
        } else if (!error) {
          const conversationUpdate = await supabase
            .from("conversations")
            .update({ status: "rejected" })
            .eq("patient_id", request.patient_id)
            .eq("practitioner_id", user?.id)
            .eq("practitioner_type", "doctor");
          error = conversationUpdate.error;
        }
      }
    }
    if (error) {
      toast({ title: bn ? "অনুরোধ আপডেট হয়নি" : "Request was not updated", description: error.message, variant: "destructive" });
    } else {
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status: action } : r)));
      toast({ title: action === "accepted" ? (bn ? "অনুরোধ গ্রহণ করা হয়েছে" : "Request accepted") : (bn ? "অনুরোধ প্রত্যাখ্যান করা হয়েছে" : "Request rejected") });
    }
    setProcessingId(null);
  };

  const pendingRequests = requests.filter((r) => r.status === "pending");

  return (
    <PractitionerDashboardView
      bn={bn}
      practitionerType="doctor"
      practitionerId={user?.id ?? ""}
      practitionerName={user?.name ?? ""}
      requests={requests}
      pendingRequests={pendingRequests}
      loading={loading}
      loadError={loadError}
      processingId={processingId}
      onRequest={handleRequest}
      onRetry={() => window.location.reload()}
    />
  );
};

export default DoctorHome;
